import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from "recharts";
import { http } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { Card, SectionTitle, Stat, SeverityChip, SEV_COLOR } from "@/components/Kit";
import { useAuth } from "@/context/AuthContext";

export default function Dashboard() {
  const { orgId } = useOrg();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [sum, setSum] = useState(null);
  const [trend, setTrend] = useState([]);
  const [pkgs, setPkgs] = useState([]);
  const [apps, setApps] = useState([]);

  useEffect(() => {
    if (!orgId) return;
    setSum(null); setTrend([]); setPkgs([]); setApps([]);
    const p = { params: { org_id: orgId } };
    http.get("/dashboard/summary", p).then((r) => setSum(r.data)).catch(() => setSum({ severity: { Critical: 0, High: 0, Medium: 0, Low: 0, Info: 0 }, open_total: 0, kev_count: 0, sla_attainment: 100, breached: 0, mttr_days: 0, app_count: 0 }));
    http.get("/dashboard/trend", p).then((r) => setTrend(r.data)).catch(() => {});
    http.get("/dashboard/top-packages", p).then((r) => setPkgs(r.data)).catch(() => {});
    if (can("appsec.applications")) http.get("/applications", p).then((r) => setApps(r.data)).catch(() => {});
  }, [orgId]);

  if (!sum) return <div className="p-8 text-[#8A8175]">Loading dashboard…</div>;

  const sev = sum.severity;
  const weighted = (a) => (a.open_findings.Critical * 100 + a.open_findings.High * 25 +
    a.open_findings.Medium * 8 + a.open_findings.Low * 2) * (a.internet_facing ? 1.5 : 1) *
    ({ Critical: 2, High: 1.5, Medium: 1, Low: 0.5 }[a.criticality] || 1);
  const ranked = [...apps].sort((x, y) => weighted(y) - weighted(x));

  const trendData = trend.filter((_, i) => i % 3 === 0); // ~30 points

  return (
    <div className="p-6 max-w-[1500px] mx-auto space-y-6 fade-up">
      <div>
        <h1 className="font-display text-3xl font-bold text-[#A5300F]">Security Posture Dashboard</h1>
        <p className="text-sm text-[#8A8175] mt-1">Organisation-wide exposure across all monitored applications.</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Stat label="Open Findings" value={sum.open_total} sub={`${sum.app_count} applications`} />
        <Stat label="Critical" value={sev.Critical} color="#A5300F" />
        <Stat label="KEV-flagged" value={sum.kev_count} sub="Known exploited" color="#A5300F" />
        <Stat label="SLA Attainment" value={`${sum.sla_attainment}%`} sub={`${sum.breached} breached`}
          color={sum.sla_attainment >= 80 ? "#2E7D32" : "#E39629"} />
        <Stat label="Mean Time to Remediate" value={`${sum.mttr_days}d`} sub="Remediated findings" />
      </div>

      <Card className="p-5">
        <SectionTitle>Open Findings — 90-day Trend</SectionTitle>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={trendData} margin={{ top: 16, right: 16, left: -10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0EAE1" />
            <XAxis dataKey="date" tick={{ fill: "#8A8175", fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
            <YAxis tick={{ fill: "#8A8175", fontSize: 10 }} allowDecimals={false} />
            <Tooltip contentStyle={{ background: "#fff", border: "1px solid #ECE6DD", borderRadius: 8, fontSize: 12 }} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            {["Critical", "High", "Medium", "Low"].map((s) => (
              <Line key={s} type="monotone" dataKey={s} stroke={SEV_COLOR[s]} strokeWidth={2} dot={false} />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <SectionTitle>Top Vulnerable Packages</SectionTitle>
          <table className="w-full text-sm mt-3">
            <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left border-b border-[#ECE6DD]">
              <th className="py-2">Package</th><th>Ecosystem</th><th className="text-right">Findings</th><th className="text-right">Top</th>
            </tr></thead>
            <tbody>
              {pkgs.map((p) => (
                <tr key={`${p.ecosystem}-${p.package}`} className="border-b border-[#F4EFE8]">
                  <td className="py-2 font-semibold">{p.package}</td>
                  <td className="text-[#8A8175]">{p.ecosystem}</td>
                  <td className="text-right stat-numeral">{p.count}</td>
                  <td className="text-right"><SeverityChip severity={p.top_severity} small /></td>
                </tr>
              ))}
              {pkgs.length === 0 && <tr><td colSpan={4} className="py-6 text-center text-[#B5AC9E]">No vulnerable packages yet.</td></tr>}
            </tbody>
          </table>
        </Card>

        <Card className="p-5">
          <SectionTitle>Applications at Risk</SectionTitle>
          <table className="w-full text-sm mt-3">
            <thead><tr className="text-[11px] uppercase tracking-wider text-[#8A8175] text-left border-b border-[#ECE6DD]">
              <th className="py-2">Application</th><th>Criticality</th><th className="text-center">Internet</th>
              <th className="text-right">C</th><th className="text-right">H</th><th className="text-right">M</th>
            </tr></thead>
            <tbody>
              {ranked.map((a) => (
                <tr key={a.id} onClick={() => navigate(`/applications/${a.id}`)}
                  className="border-b border-[#F4EFE8] hover:bg-[#FBF7F1] cursor-pointer" data-testid={`risk-app-${a.id}`}>
                  <td className="py-2 font-semibold">{a.name}</td>
                  <td className="text-[#5C554C]">{a.criticality}</td>
                  <td className="text-center">{a.internet_facing ? "Yes" : "No"}</td>
                  <td className="text-right font-bold" style={{ color: "#A5300F" }}>{a.open_findings.Critical}</td>
                  <td className="text-right" style={{ color: "#D55816" }}>{a.open_findings.High}</td>
                  <td className="text-right" style={{ color: "#E39629" }}>{a.open_findings.Medium}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}
